import { Root as Label } from "@radix-ui/react-label";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as React from "react";
import { tv, type VariantProps } from "tailwind-variants";

const RadioVariant = tv({
  slots: {
    wrapperStyle: "body-md",
    radioWrapper: "flex items-center gap-2",
    inputStyles: [
      "bg-surface-default text-border-default hover:text-border-highlight-default group-hover/menu-item:text-border-highlight-default focus-visible:outline-focused-black disabled:border-border-disabled aria-checked:text-border-highlight-default box-border flex h-5 w-5 items-center justify-center overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-0 duration-200 aria-checked:duration-100 transition-[box-shadow] ease-expressive-in-out aria-checked:ease-in shadow-[inset_0_0_0_2px_currentColor]",
      "scale-105 rounded-xl aria-checked:shadow-[inset_0_0_0_5px_currentColor]",
    ],
    labelStyles: "flex flex-col",
    helperStyles: "text-text-subdued body-sm",
  },
  variants: {
    variant: {
      default: {},
      disabled: {
        inputStyles:
          "border-border-disabled data-[state=checked]:border-icon-disabled",
        labelStyles: "text-text-disabled",
        helperStyles: "text-current",
      },
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface RadioProps
  extends VariantProps<typeof RadioVariant>,
    Omit<RadioGroup.RadioGroupProps, "asChild"> {
  elements: {
    label: string | React.JSX.Element;
    value: string;
    helperText?: string | React.JSX.Element;
  }[];
  label: string | React.JSX.Element;
}

interface RadioElementProps
  extends RadioGroup.RadioGroupItemProps,
    Pick<RadioProps["elements"][number], "helperText" | "label" | "value">,
    Pick<RadioProps, "variant"> {}

const RadioElement: React.FC<RadioElementProps> = ({
  label,
  helperText,
  value,
  variant,
  ...props
}) => {
  const { inputStyles, labelStyles, helperStyles, radioWrapper } = RadioVariant(
    {
      variant,
    },
  );
  const id = React.useId();
  return (
    <label htmlFor={id} className={radioWrapper()}>
      <div className="w-6 h-6 flex justify-center items-center self-start">
        <RadioGroup.Item
          id={id}
          value={value}
          className={inputStyles()}
          disabled={variant === "disabled"}
          {...props}
        />
      </div>
      <Label htmlFor={id} className={labelStyles()}>
        {label}
        {helperText && <span className={helperStyles()}>{helperText}</span>}
      </Label>
    </label>
  );
};

const Radio = ({
  variant,
  elements,
  required,
  onValueChange,
  value,
  color,
  defaultValue,
  className,
  ...props
}: RadioProps) => {
  const { wrapperStyle } = RadioVariant({
    variant,
  });
  return (
    <RadioGroup.Root
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      value={value}
      required={required}
      className={wrapperStyle({ className })}
      {...props}
    >
      <div className="space-y-3">
        {props.label ? (
          <span className="text-sm text-text-default">{props.label}</span>
        ) : null}
        {elements.map((element) => (
          <RadioElement
            key={element.value}
            variant={variant}
            color={color}
            {...element}
          />
        ))}
      </div>
    </RadioGroup.Root>
  );
};
Radio.displayName = "Radio";
export default Radio;
